import useOptimiceImageFile from "@rf-hooks/useOptimiseImageFile";
import { HGenerateFileData } from "@rf-l1/file/helpers/create-file-data";
import { IFileData } from "@rf-l1/file/helpers/create-file-data";
import { getValidationErrorFileDataEnhanced } from "@rf-l1/file/helpers/getValidationError";
import { AllowedFileExtensions } from "@rf-l1/file/model/types.model";
import { useCallback } from "react";
import toast from "react-hot-toast";

interface IUseProcessFileProps {
  oldFiles: IFileData[];
  maxSize: number;
  minSize: number;
  allowedTypes: AllowedFileExtensions[];
  multiple?: boolean;
  urlUpload?: string;
}

const useProcessFile = (props: IUseProcessFileProps) => {
  const { optimiceImageFile } = useOptimiceImageFile();

  const getFileDataFingerprint = (data: IFileData): string => {
    const anyData = data as any;
    // Para archivos existentes del servidor, el url es estable
    if (anyData.isNewFile === false && typeof anyData.url === "string") {
      return `url::${anyData.url}`;
    }
    // Para archivos locales, usar metadatos del File si está disponible
    const f: File | undefined = anyData.file;
    const lastModified = (f as any)?.lastModified ?? 0;
    const name: string = anyData.name ?? f?.name ?? "";
    const size: number = anyData.size ?? f?.size ?? 0;
    const type: string = anyData.type ?? f?.type ?? "";
    return [name, size, type, lastModified].join("::");
  };

  const processFile = async (file: File): Promise<IFileData | null> => {
    try {
      const error = await getValidationErrorFileDataEnhanced(
        file,
        props.maxSize,
        props.minSize,
        props.allowedTypes
      );

      if (error) {
        toast.error(error, {
          duration: 5000,
          position: "top-right"
        });
        return null;
      }

      let processedFile = file;
      if (file.type.includes("image")) {
        processedFile = await optimiceImageFile(file);
      }

      const newFile = HGenerateFileData({
        file: processedFile
      });

      if (!newFile) {
        throw new Error("Error al generar el archivo");
      }

      return newFile;
    } catch (error) {
      toast.error(`Error al procesar el archivo: ${file.name}`);
      return null;
    }
  };

  const processFiles = useCallback(
    async (
      files: File[],
      currentOldFiles?: IFileData[]
    ): Promise<IFileData[]> => {
      // Siempre usa la lista más reciente disponible para evitar desincronización
      const baseOldFiles = currentOldFiles ?? props.oldFiles ?? [];

      const results = await Promise.allSettled(files.map(file => processFile(file)));

      const successfulFiles = results
        .filter(
          (result): result is PromiseFulfilledResult<IFileData> =>
            result.status === "fulfilled" && result.value !== null
        )
        .map(result => result.value);

      // Deduplicación: 1) dentro del lote actual 2) contra los archivos ya existentes
      const seenInBatch = new Set<string>();
      const uniqueInBatch = successfulFiles.filter(item => {
        const fp = getFileDataFingerprint(item);
        if (seenInBatch.has(fp)) return false;
        seenInBatch.add(fp);
        return true;
      });

      const existingFingerprints = new Set<string>(
        (baseOldFiles ?? []).map(getFileDataFingerprint)
      );

      const newUniqueFiles = uniqueInBatch.filter(item => {
        const fp = getFileDataFingerprint(item);
        return !existingFingerprints.has(fp);
      });

      if (newUniqueFiles.length === 0) {
        toast.success("No se agregaron archivos duplicados", {
          duration: 2500,
          position: "top-right"
        });
        // No agregamos nada nuevo, devolvemos el estado previo
        return baseOldFiles ?? [];
      }

      // Si multiple es false, reemplazamos por el primer nuevo archivo
      return props.multiple === false
        ? [newUniqueFiles[0]]
        : [...baseOldFiles, ...newUniqueFiles];
    },
    [props.oldFiles, props.multiple]
  );

  return processFiles;
};

export default useProcessFile;
